/**
 * Выбор прогона распознавания из списка ревизии (S29).
 *
 * ## Почему это отдельный модуль, а не `at(-1)` по месту
 *
 * `listRecognitionRuns` сортирует `desc(started_at)` — новейший прогон стоит
 * ПЕРВЫМ. `PipelineBar` брал `runs.at(-1)`, то есть самый СТАРЫЙ, и читал по
 * нему режим прогона: жёлтая плашка «Портал в режиме сравнения провайдеров»
 * описывала давний ручной прогон, а не тот, который человек только что запустил.
 * Ошибка держалась незаметно, потому что рядом стоит `bundles.at(-1)`, и он
 * верен — `listBundles` сортирует ASC.
 *
 * Порядок списка перестаёт быть неписаным знанием компонента: он назван здесь
 * один раз и закрыт тестом. Сортировка на клиенте не повторяется — повторить её
 * значило бы завести второе мнение о том же порядке; вместо этого выбор сведён
 * к «первый» и «последний по времени», и оба выражены через `startedAt`, а не
 * через позицию в массиве. Тогда смена сортировки на сервере не меняет ответа.
 */
import type { RecognitionRun } from '../../api/types.js';

/**
 * Новейший прогон ревизии — тот, чей `startedAt` больше всех.
 *
 * Сравнение по времени, а не взятие `[0]`: позиция в массиве — договорённость
 * с сервером, которую компонент не может проверить, а `startedAt` приходит в
 * самой строке. Одинаковое время у двух прогонов невозможно (§6.2: незавершённый
 * прогон по ревизии разметки ровно один), но при равенстве побеждает более
 * ранний в списке — тот же порядок, что у сервера.
 */
export function newestRecognitionRun(
  runs: readonly RecognitionRun[] | undefined,
): RecognitionRun | null {
  let newest: RecognitionRun | null = null;
  for (const run of runs ?? []) {
    if (newest === null || Date.parse(run.startedAt) > Date.parse(newest.startedAt)) {
      newest = run;
    }
  }
  return newest;
}

/**
 * Идущий прогон — он один по доменному инварианту §6.2.
 *
 * Если их всё же двое (рассинхрон после ручного вмешательства), берётся
 * новейший: прогресс, показанный по старому, вводил бы в заблуждение сильнее,
 * чем его отсутствие.
 */
export function runningRecognitionRun(
  runs: readonly RecognitionRun[] | undefined,
): RecognitionRun | null {
  return newestRecognitionRun((runs ?? []).filter((run) => run.status === 'running'));
}

/**
 * Идёт ли прогон в shadow-режиме — по его собственному снимку настроек.
 *
 * Снимок — единственный честный источник: он пиннится на старте (ADR-0007), и
 * прогон, начатый до переключения настройки, идёт по своему режиму. Форма
 * снимка объявлена как `unknown`, потому что версий у него две; читается ровно
 * одно поле, и отсутствие его означает «не dry-run», а не «неизвестно».
 */
export function isDryRun(snapshot: unknown): boolean {
  if (typeof snapshot !== 'object' || snapshot === null) return false;
  return (snapshot as { dryRun?: unknown }).dryRun === true;
}
